/**
 * Project Navigation Storage Utility
 * 
 * Manages localStorage for tracking user's last position within each project
 * (mode and thread_id) to restore their context when returning to a project.
 */

const STORAGE_KEY = 'projectNavigationState';

export interface ProjectPosition {
  currentMode: 'cab' | 'design' | 'build';
  designThreadId: string | null;
}

interface ProjectNavigationState {
  [projectId: string]: ProjectPosition;
}

/**
 * Get all project navigation states from localStorage
 */
const getStorageData = (): ProjectNavigationState => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error('Error reading project navigation state from localStorage:', error);
    return {};
  }
};

/**
 * Save all project navigation states to localStorage
 */
const setStorageData = (data: ProjectNavigationState): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving project navigation state to localStorage:', error);
  }
};

/**
 * Save the current position for a project
 * 
 * @param projectId - The project ID
 * @param mode - The current mode ('design' or 'build')
 * @param threadId - The current thread ID (only used in design mode)
 */
export const saveProjectPosition = (
  projectId: string,
  mode: 'cab' | 'design' | 'build',
  threadId: string | null = null
): void => {
  if (!projectId) {
    console.warn('Cannot save project position: projectId is required');
    return;
  }

  const data = getStorageData();
  const existingPosition = data[projectId];
  
  // Preserve existing designThreadId when switching to build mode
  const preservedDesignThreadId = existingPosition?.designThreadId || null;
  
  data[projectId] = {
    currentMode: mode,
    designThreadId: mode === 'design' ? (threadId || preservedDesignThreadId) : preservedDesignThreadId,
  };
  setStorageData(data);
};

/**
 * Get the saved position for a project
 * 
 * @param projectId - The project ID
 * @returns The saved position or null if not found
 */
export const getProjectPosition = (projectId: string): ProjectPosition | null => {
  if (!projectId) {
    return null;
  }

  const data = getStorageData();
  return data[projectId] || null;
};

/**
 * Clear the saved position for a specific project
 * 
 * @param projectId - The project ID
 */
export const clearProjectPosition = (projectId: string): void => {
  if (!projectId) {
    return;
  }

  const data = getStorageData();
  delete data[projectId];
  setStorageData(data);
};

/**
 * Clear all saved project positions
 */
export const clearAllProjectPositions = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing project navigation state from localStorage:', error);
  }
};
